import React, { useState, useEffect } from 'react';
import { Space, Tooltip, Modal, Button } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { EyeOutlined, LinkOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { SocialMediaPost, SocialMediaApiResponse } from '../../common/social-media.interface';
import { socialMediaApi } from '../../api/social-media/social-media.api';
import CTable from '../../components/table/CTable';
import { QUERY_PARAM } from '../../constants/get-api.constant';
import './social-media.styles.scss';

const SocialMediaPage: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<SocialMediaPost | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [posts, setPosts] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [textSearch, setTextSearch] = useState('');
  const [currentSearchValue, setCurrentSearchValue] = useState<any>({
    page: 1,
    pageSize: QUERY_PARAM.size,
  });

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const columns: ColumnType<any>[] = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: '80px',
      align: 'center',
    },
    {
      title: 'Ảnh',
      dataIndex: 'imageContents',
      key: 'imageContents',
      width: '100px',
      align: 'center',
      render: (images: string[], record: any) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {images && images.length > 0 ? (
            <img 
              src={images[0]}
              alt={record.title}
              style={{ 
                width: 60, 
                height: 40, 
                objectFit: 'cover', 
                borderRadius: 4,
                border: '1px solid #f0f0f0'
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div style={{ 
              width: 60, 
              height: 40, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 4, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #f0f0f0',
              color: '#bfbfbf',
              fontSize: 12
            }}>
              Không có ảnh
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      width: '280px',
      render: (text: string) => (
        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
          {truncateText(text, 70)}
        </div>
      ),
    },
    {
      title: 'Nguồn',
      dataIndex: 'source',
      key: 'source',
      width: '140px',
      render: (text: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src={record.sourceInfo.avatar}
            alt={record.sourceInfo.name}
            style={{ width: 24, height: 24, borderRadius: '50%', marginRight: 8 }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/favicon.ico';
            }}
          />
          <span>{record.sourceInfo.name}</span>
        </div>
      ),
    },
    {
      title: 'Danh mục',
      dataIndex: 'category',
      key: 'category',
      width: '110px',
    },
    {
      title: 'Thời gian',
      dataIndex: 'postedAt',
      key: 'postedAt',
      width: '140px',
      render: (text: string) => formatDate(text),
    },
    {
      title: 'Lượt xem',
      dataIndex: 'views',
      key: 'views',
      width: '90px',
      align: 'center',
    },
    {
      title: 'Thao tác',
      dataIndex: 'action',
      key: 'action',
      width: '120px',
      align: 'center',
      render: (text: any, record: any) => (
        <Space size="middle">
          <Tooltip title="Xem chi tiết">
            <EyeOutlined 
              style={{ color: '#1890ff', cursor: 'pointer' }}
              onClick={() => handleViewDetail(record)} 
            />
          </Tooltip>
          <Tooltip title="Mở bài gốc">
            <LinkOutlined 
              style={{ color: '#52c41a', cursor: 'pointer' }}
              onClick={() => window.open(record.link, '_blank')} 
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const fetchPosts = async (params: any) => {
    setLoading(true);
    try {
      const response: SocialMediaApiResponse = await socialMediaApi.getPaginatedPosts(
        params.page || 1,
        params.pageSize || QUERY_PARAM.size,
        'postedAt',
        true
      );
      
      const formattedPosts = response.docs.map((post, index) => ({
        ...post,
        index: (params.page - 1) * (params.pageSize || QUERY_PARAM.size) + index + 1,
        key: post.id,
      }));

      setPosts(formattedPosts);
      setTotalRecords(response.total);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(currentSearchValue);
  }, [currentSearchValue]);

  const onChangeInput = (event: any) => {
    setTextSearch(event.target.value);
  };

  const onSearch = () => {
    // Filter posts based on search text
    if (!textSearch.trim()) {
      fetchPosts(currentSearchValue);
      return;
    }

    setLoading(true);
    setTimeout(() => {
      const filteredPosts = posts.filter(post => 
        post.title.toLowerCase().includes(textSearch.toLowerCase()) ||
        post.sourceInfo.name.toLowerCase().includes(textSearch.toLowerCase()) ||
        post.category.toLowerCase().includes(textSearch.toLowerCase())
      );
      setPosts(filteredPosts);
      setTotalRecords(filteredPosts.length);
      setLoading(false);
    }, 300);
  };

  const onChangePagination = (page: number) => {
    const newSearchValue = {
      ...currentSearchValue,
      page: page,
    };
    setCurrentSearchValue(newSearchValue);
  };

  const handleViewDetail = (post: SocialMediaPost) => {
    setSelectedPost(post);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedPost(null);
  };

  const openOriginalLink = () => {
    if (selectedPost) {
      window.open(selectedPost.link, '_blank');
    }
  };

  return (
    <motion.div
      className="sketch-main"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="table-area">
        <CTable
          tableMainTitle="Danh sách bài viết mạng xã hội"
          allowTextSearch={true}
          onChangeInput={onChangeInput}
          onSearch={onSearch}
          titleOfColumnList={columns}
          data={posts}
          totalRecord={totalRecords}
          onChangePagination={onChangePagination}
          allowAddObject={false}
          textSearch="Tìm kiếm bài viết..."
          pageSize={QUERY_PARAM.size}
          size={700}
          loading={loading}
        />
      </div>

      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedPost?.title}</span>
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={openOriginalLink}
              size="small"
            >
              Mở bài gốc
            </Button>
          </div>
        }
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        className="social-media-modal"
      >
        {selectedPost && (
          <div>
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img 
                    src={selectedPost.sourceInfo.avatar}
                    alt={selectedPost.sourceInfo.name}
                    style={{ width: 40, height: 40, borderRadius: '50%', marginRight: 12 }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/favicon.ico';
                    }}
                  />
                  <div>
                    <h4 style={{ margin: 0 }}>{selectedPost.sourceInfo.name}</h4>
                    <p style={{ margin: 0, color: '#8c8c8c' }}>{formatDate(selectedPost.postedAt)}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <span><i className="fas fa-eye"></i> {selectedPost.views}</span>
                  <span><i className="fas fa-heart"></i> {selectedPost.likes}</span>
                  <span><i className="fas fa-share"></i> {selectedPost.shares}</span>
                </div>
              </div>
            </div>

            {selectedPost.imageContents.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {selectedPost.imageContents.map((image, index) => (
                  <img 
                    key={index}
                    src={image}
                    alt={`${selectedPost.title} - ${index + 1}`}
                    style={{ width: '100%', maxHeight: 400, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ))}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <p style={{ lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                {selectedPost.editedTextContent || selectedPost.textContent}
              </p>
            </div>

            <div style={{ paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <span style={{ fontWeight: 600, marginRight: 12 }}>Danh mục:</span>
              <span style={{ background: '#f6f6f6', padding: '4px 12px', borderRadius: 16, marginRight: 8 }}>
                {selectedPost.category}
              </span>
              <span style={{ background: '#e6f7ff', color: '#1890ff', padding: '4px 12px', borderRadius: 16 }}>
                {selectedPost.type}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
};

export default SocialMediaPage;
